/**
 * Small style layer for the marketing site. It carries only what Tailwind
 * utilities / semantic tokens can't express cleanly:
 *
 * 1. A `.pd-marketing` scope that re-asserts the light "Signal Blue" palette,
 *    plus `.pd-marketing-surface` — the same tokens without the paint, for
 *    globally-mounted overlays (the cookie banner and its dialog) that render
 *    outside the page wrapper and would otherwise show the app's dark theme on
 *    a light marketing page.
 *    The app's ThemeProvider defaults to `system`, so a visitor on a dark OS
 *    would otherwise flip every `bg-background` / `text-foreground` token to the
 *    dark palette and break the light marketing design. Dark sections opt back
 *    in explicitly via a nested `.dark` wrapper (which re-declares the tokens),
 *    exactly as the design intends.
 * 2. The brand gradient used by CTAs, badges and the logo mark — its magenta
 *    stop has no semantic token, so it lives as a utility (per the handoff).
 * 3. The gentle float keyframes for the hero's floating cards.
 *
 * Rendered once per page; React 19 dedupes on the `href`.
 */
const CSS = `
.pd-marketing,
.pd-marketing-surface{
  color-scheme: light;
  --background: oklch(0.99 0.004 264);
  --foreground: oklch(0.21 0.03 264);
  --card: oklch(1 0 0);
  --card-foreground: oklch(0.21 0.03 264);
  --popover: oklch(1 0 0);
  --popover-foreground: oklch(0.21 0.03 264);
  --primary: oklch(0.55 0.2 264);
  --primary-foreground: oklch(0.985 0 0);
  --secondary: oklch(0.95 0.03 264);
  --secondary-foreground: oklch(0.33 0.07 264);
  --muted: oklch(0.965 0.014 264);
  --muted-foreground: oklch(0.5 0.03 264);
  --accent: oklch(0.92 0.05 264);
  --accent-foreground: oklch(0.33 0.08 264);
  --border: oklch(0.91 0.016 264);
  --input: oklch(0.91 0.016 264);
  --ring: oklch(0.55 0.2 264);
  color: var(--foreground);
}
/*
 * Only the page wrapper paints a background. pd-marketing-surface takes the
 * tokens and the text colour, for elements that must adopt the marketing
 * palette while keeping their own bg-* class - a portalled overlay, for
 * instance, where setting background-color here would tie with Tailwind on
 * specificity and be decided by source order.
 *
 * Colour is shared rather than left out, because inheritance is not a
 * specificity contest: any descendant that declares its own text-* still wins,
 * while one that declares none needs this or it inherits from <body>, which
 * follows the app's theme. That gap made the banner's ghost and outline buttons
 * - the two variants with no colour of their own until :hover - render
 * near-white on a near-white bar.
 */
.pd-marketing{
  background-color: var(--background);
}
/*
 * The dark class stays on <html> even here, because next-themes tracks the
 * app's theme and the marketing design is light regardless. Swapping the
 * variables is therefore not quite enough: a Tailwind dark: utility is
 * class-based, so it still fires inside this scope and reads the LIGHT token it
 * was written to avoid. The switch thumb is the visible case - dark:
 * bg-foreground painted a near-black knob on a pale track.
 *
 * Restated here rather than fixed in packages/ui, because the component is
 * right for every surface where .dark and the dark palette agree, which is
 * everywhere except this one.
 */
.pd-marketing-surface [data-slot="switch"] [data-slot="switch-thumb"]{
  background-color: var(--background);
}
html:has(.pd-marketing){scroll-behavior:smooth}
.pd-brand-grad{background-image:linear-gradient(135deg,oklch(0.6 0.15 246),oklch(0.5 0.2 280))}
.pd-brand-grad-soft{background-image:linear-gradient(140deg,oklch(0.62 0.16 248),oklch(0.56 0.19 276))}
@keyframes pd-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}
.pd-float{animation:pd-float 6s ease-in-out infinite}
.pd-float-slow{animation:pd-float 7s ease-in-out infinite}
@media (prefers-reduced-motion:reduce){.pd-float,.pd-float-slow{animation:none}}
.pd-marketing-dropdown{
  color-scheme:light;
  --background:oklch(0.99 0.004 264);
  --foreground:oklch(0.21 0.03 264);
  --popover:oklch(1 0 0);
  --popover-foreground:oklch(0.21 0.03 264);
  --muted:oklch(0.965 0.014 264);
  --muted-foreground:oklch(0.5 0.03 264);
  --border:oklch(0.91 0.016 264);
  --accent:oklch(0.92 0.05 264);
  --accent-foreground:oklch(0.33 0.08 264);
  --destructive:oklch(0.577 0.245 27.325);
  background-color:oklch(1 0 0);
  color:oklch(0.21 0.03 264);
}
`;

export function MarketingStyles() {
  return (
    <style
      href="pd-marketing-styles"
      precedence="medium"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: static, author-controlled stylesheet
      dangerouslySetInnerHTML={{ __html: CSS }}
    />
  );
}
